import { API_BASE_URL } from './config';
import { apiFetch } from './apiFetch';

export type TaskStatus = 'todo' | 'in_progress' | 'in_review' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';
export type SprintStatus = 'planning' | 'active' | 'completed';
export type ProjectStatus = 'active' | 'archived';

export interface Task {
  id: string;
  sprintId: string | null;
  projectId: string;
  companyId: string;
  title: string;
  description: string | null;
  assigneeUserId: string | null;
  reporterUserId: string | null;
  status: TaskStatus;
  storyPoints: number | null;
  priority: TaskPriority;
  dueDate: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Sprint {
  id: string;
  projectId: string;
  companyId: string;
  name: string;
  goal: string | null;
  startDate: string | null;
  endDate: string | null;
  status: SprintStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  ownerUserId: string | null;
  status: ProjectStatus;
  createdAt: string;
  updatedAt: string;
  activeSprint?: Sprint | null;
  openTaskCount?: number;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// ── Projects ──────────────────────────────────────────────────────────────────

export async function listProjects(includeArchived = false): Promise<Project[]> {
  const res = await apiFetch(
    `${API_BASE_URL}/api/tasks/projects${includeArchived ? '?includeArchived=true' : ''}`,
  );
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.projects;
}

export async function createProject(
  input: { name: string; description?: string } = { name: '' },
): Promise<Project> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/projects`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.project;
}

export async function archiveProject(projectId?: string): Promise<void> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/projects/${projectId}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ status: 'archived' }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
}

export async function updateProject(
  projectId: string,
  input: { name?: string; goal?: string; startDate?: string; endDate?: string },
): Promise<Project> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/projects/${projectId}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.project;
}

// ── Sprints ───────────────────────────────────────────────────────────────────

export async function listSprints(projectId?: string): Promise<Sprint[]> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/projects/${projectId}/sprints`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.sprints;
}

export async function createSprint(
  projectId?: string,
  input: { name: string; goal?: string; startDate?: string; endDate?: string } = { name: '' },
): Promise<Sprint> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/projects/${projectId}/sprints`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.sprint;
}

export async function updateSprint(
  sprintId?: string,
  input: Partial<{ name: string; goal: string; startDate: string; endDate: string; status: SprintStatus }> = {},
): Promise<Sprint> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/sprints/${sprintId}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.sprint;
}

export async function completeSprint(sprintId?: string): Promise<Sprint> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/sprints/${sprintId}/complete`, {
    method: 'POST',
    headers: JSON_HEADERS,
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.sprint;
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

export async function listTasks(sprintId?: string): Promise<Task[]> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/sprints/${sprintId}/tasks`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.tasks;
}

export async function createTask(
  sprintId?: string,
  input: {
    projectId: string;
    title: string;
    description?: string;
    assigneeUserId?: string;
    storyPoints?: number;
    priority?: TaskPriority;
    dueDate?: string;
  } = { projectId: '', title: '' },
): Promise<Task> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/sprints/${sprintId}/tasks`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.task;
}

export async function updateTask(
  taskId?: string,
  input: Partial<{
    title: string;
    description: string;
    assigneeUserId: string;
    status: TaskStatus;
    storyPoints: number;
    priority: TaskPriority;
    dueDate: string;
    sprintId: string;
  }> = {},
): Promise<Task> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/tasks/${taskId}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.task;
}

export async function deleteTask(taskId?: string): Promise<void> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/tasks/${taskId}`, {
    method: 'DELETE',
    headers: JSON_HEADERS,
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

export const PRIORITY_DOT: Record<TaskPriority, string> = {
  low: 'bg-gray-400',
  medium: 'bg-blue-500',
  high: 'bg-orange-500',
  critical: 'bg-red-500',
};

export const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'Todo',
  in_progress: 'In Progress',
  in_review: 'In Review',
  done: 'Done',
};

export const SPRINT_STATUS_COLORS: Record<SprintStatus, string> = {
  planning: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
};

// ── Sprint metrics types ───────────────────────────────────────────────────────

export interface SprintAssigneeSummary {
  userId: string;
  name: string;
  totalTasks: number;
  doneTasks: number;
  deliveryRate: number;
  velocity: number;
  ticketCompletionRate: number;
}

export interface RecordedSprintMetric {
  userId: string;
  userName: string;
  metricCode: string;
  metricName: string;
  value: number;
  capturedAt: string;
}

export interface SprintMetricsSummary {
  sprintId: string;
  totalTasks: number;
  doneTasks: number;
  deliveryRate: number;
  velocity: number;
  ticketCompletionRate: number;
  statusCounts: Record<string, number>;
  byAssignee: SprintAssigneeSummary[];
  recordedMetrics: RecordedSprintMetric[];
}

// ── Sprint fetch helpers ───────────────────────────────────────────────────────

export async function getSprint(sprintId?: string): Promise<Sprint> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/sprints/${sprintId}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.sprint;
}

export async function getSprintMetrics(sprintId?: string): Promise<SprintMetricsSummary> {
  const res = await apiFetch(`${API_BASE_URL}/api/tasks/sprints/${sprintId}/metrics`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed');
  return data.data.metrics;
}
